import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  HelpCircle, 
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Database,
  Server,
  Globe,
  Smartphone,
  Mail,
  FileText,
  Download,
  ExternalLink
} from 'lucide-react';

const AdminHelpPage = () => {
  const [expandedSection, setExpandedSection] = useState(null);

  const troubleshootingGuides = [
    {
      id: 'quote-form',
      title: 'Quote Form Not Submitting',
      icon: FileText,
      severity: 'medium',
      symptoms: [
        'Form shows "Loading" indefinitely',
        'Error message appears after submission',
        'Page refreshes without saving data'
      ],
      solutions: [
        {
          step: 1,
          title: 'Check Internet Connection',
          description: 'Ensure you have a stable internet connection. Try loading another website.'
        },
        {
          step: 2,
          title: 'Clear Browser Cache',
          description: 'Go to Settings > Privacy > Clear Browsing Data. Select "Cached images and files" and clear.'
        },
        {
          step: 3,
          title: 'Try a Different Browser',
          description: 'If using Chrome, try Firefox or Edge. This helps identify browser-specific issues.'
        },
        {
          step: 4,
          title: 'Check Browser Console',
          description: 'Press F12, go to Console tab. Look for red error messages and note them down.'
        },
        {
          step: 5,
          title: 'Escalate if Needed',
          description: 'If the issue persists, contact your developer with the error messages from Step 4.'
        }
      ]
    },
    {
      id: 'slow-loading',
      title: 'Slow Page Loading',
      icon: RefreshCw,
      severity: 'low',
      symptoms: [
        'Pages take more than 5 seconds to load',
        'Images appear slowly',
        'Spinner shows for extended periods'
      ],
      solutions: [
        {
          step: 1,
          title: 'Test Your Internet Speed',
          description: 'Visit speedtest.net to check your connection speed. You need at least 5 Mbps for optimal performance.'
        },
        {
          step: 2,
          title: 'Close Unnecessary Tabs',
          description: 'Having many browser tabs open can slow down performance. Close tabs you\'re not using.'
        },
        {
          step: 3,
          title: 'Clear Browser Cache',
          description: 'Cached data can become corrupted. Clear cache and try again.'
        },
        {
          step: 4,
          title: 'Check Server Status',
          description: 'If multiple users report slowness, the server might be under heavy load. Wait 15-30 minutes and try again.'
        }
      ]
    },
    {
      id: 'broken-links',
      title: 'Broken Links or 404 Errors',
      icon: XCircle,
      severity: 'medium',
      symptoms: [
        'Clicking a link shows "Page Not Found"',
        '404 error message appears',
        'Links lead to blank pages'
      ],
      solutions: [
        {
          step: 1,
          title: 'Refresh the Page',
          description: 'Press F5 or click the refresh button. Sometimes pages fail to load properly.'
        },
        {
          step: 2,
          title: 'Check the URL',
          description: 'Look at the address bar. If it looks wrong or has typos, go back to the homepage and navigate again.'
        },
        {
          step: 3,
          title: 'Clear Browser Cache',
          description: 'Old cached data might be pointing to outdated URLs.'
        },
        {
          step: 4,
          title: 'Note the Broken Link',
          description: 'Write down which page you were on and which link was broken. Report this to your developer.'
        }
      ]
    },
    {
      id: 'login-issues',
      title: 'Cannot Login or Stay Logged In',
      icon: AlertTriangle,
      severity: 'high',
      symptoms: [
        'Login fails even with correct password',
        'Getting logged out frequently',
        '"Invalid credentials" error'
      ],
      solutions: [
        {
          step: 1,
          title: 'Verify Credentials',
          description: 'Double-check your email and password. Use the "Show Password" option if available.'
        },
        {
          step: 2,
          title: 'Reset Password',
          description: 'Try resetting your password using the "Forgot Password" link on the login page.'
        },
        {
          step: 3,
          title: 'Check Caps Lock',
          description: 'Ensure Caps Lock is not accidentally enabled when typing your password.'
        },
        {
          step: 4,
          title: 'Clear Cookies',
          description: 'Go to browser settings and clear cookies for this website specifically.'
        },
        {
          step: 5,
          title: 'Try Incognito Mode',
          description: 'Open an incognito/private window and try logging in. This bypasses cached data.'
        }
      ]
    },
    {
      id: 'database-connection',
      title: 'Backend/Database Connection Issues',
      icon: Database,
      severity: 'high',
      symptoms: [
        'Data not loading on dashboard',
        '"Failed to fetch" errors',
        'Information appears outdated or missing'
      ],
      solutions: [
        {
          step: 1,
          title: 'Wait and Retry',
          description: 'Database issues are often temporary. Wait 5 minutes and refresh the page.'
        },
        {
          step: 2,
          title: 'Check Health Endpoint',
          description: 'Visit /api/health in your browser. If it shows "healthy", the server is running.'
        },
        {
          step: 3,
          title: 'Check Hosting Dashboard',
          description: 'If using Vercel/Netlify, log into their dashboard to check if all services are running.'
        },
        {
          step: 4,
          title: 'Contact Developer',
          description: 'If issues persist for more than 30 minutes, escalate to your developer immediately.'
        }
      ]
    },
    {
      id: 'mobile-display',
      title: 'Mobile Display Issues',
      icon: Smartphone,
      severity: 'low',
      symptoms: [
        'Layout looks broken on phone',
        'Text is too small to read',
        'Buttons are hard to tap'
      ],
      solutions: [
        {
          step: 1,
          title: 'Try Portrait and Landscape',
          description: 'Rotate your phone to see if the layout improves in different orientations.'
        },
        {
          step: 2,
          title: 'Update Your Browser',
          description: 'Make sure your mobile browser (Chrome, Safari) is updated to the latest version.'
        },
        {
          step: 3,
          title: 'Clear Mobile Cache',
          description: 'In your mobile browser settings, clear cache and browsing data.'
        },
        {
          step: 4,
          title: 'Try Desktop Mode',
          description: 'If a feature is critical, you can enable "Desktop site" mode in your mobile browser menu.'
        }
      ]
    }
  ];

  const quickTips = [
    { icon: RefreshCw, tip: 'Always try refreshing the page first (F5 or Ctrl+R)' },
    { icon: Globe, tip: 'Check your internet connection if features aren\'t loading' },
    { icon: Server, tip: 'Visit /api/health to verify the server is running' },
    { icon: Mail, tip: 'Document error messages before contacting support' }
  ];

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'text-red-600 bg-red-100';
      case 'medium': return 'text-amber-600 bg-amber-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-slate-600 bg-slate-100';
    }
  };

  const downloadPDF = () => {
    // Generate a simple text version for download
    let content = "ARCHON INSURANCE - TROUBLESHOOTING GUIDE\n";
    content += "==========================================\n\n";
    
    troubleshootingGuides.forEach(guide => {
      content += `${guide.title.toUpperCase()}\n`;
      content += "-".repeat(guide.title.length) + "\n";
      content += "Symptoms:\n";
      guide.symptoms.forEach(s => content += `  • ${s}\n`);
      content += "\nSolutions:\n";
      guide.solutions.forEach(sol => {
        content += `  ${sol.step}. ${sol.title}\n`;
        content += `     ${sol.description}\n\n`;
      });
      content += "\n";
    });
    
    content += "\nQUICK TIPS\n";
    content += "----------\n";
    quickTips.forEach(tip => content += `• ${tip.tip}\n`);
    
    content += "\n\nWHEN TO CONTACT YOUR DEVELOPER\n";
    content += "-------------------------------\n";
    content += "• Issue persists after trying all solutions\n";
    content += "• Multiple users report the same problem\n";
    content += "• Server health check fails\n";
    content += "• Data appears corrupted or missing\n";
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'archon-troubleshooting-guide.txt';
    a.click();
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-help-page">
        <div className="mb-6">
          <Link to="/admin" className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900 mb-4">
            <ChevronLeft className="h-4 w-4 mr-1" /> Back to Dashboard
          </Link>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Help & Troubleshooting</h1>
              <p className="text-slate-600 mt-1">Common issues and how to solve them</p>
            </div>
            <Button onClick={downloadPDF} variant="outline" className="flex items-center gap-2">
              <Download className="h-4 w-4" /> Download Guide
            </Button>
            <a 
              href="/archon-cms-guide.md" 
              download="archon-cms-guide.md"
              className="inline-flex items-center gap-2 px-4 py-2 border rounded-md text-sm font-medium hover:bg-gray-50"
            >
              <FileText className="h-4 w-4" /> CMS User Guide
            </a>
          </div>
        </div>

        {/* Quick Tips */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" /> Quick Tips
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 gap-4">
              {quickTips.map((item, index) => (
                <div key={index} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                  <item.icon className="h-5 w-5 text-slate-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-slate-700">{item.tip}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Troubleshooting Guides */}
        <div className="space-y-4">
          {troubleshootingGuides.map((guide) => (
            <Card key={guide.id} className="overflow-hidden">
              <button
                className="w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                onClick={() => setExpandedSection(expandedSection === guide.id ? null : guide.id)}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${getSeverityColor(guide.severity)}`}>
                    <guide.icon className="h-5 w-5" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-medium text-slate-900">{guide.title}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded ${getSeverityColor(guide.severity)}`}>
                      {guide.severity} priority
                    </span>
                  </div>
                </div>
                {expandedSection === guide.id ? (
                  <ChevronDown className="h-5 w-5 text-slate-400" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-slate-400" />
                )}
              </button>
              
              {expandedSection === guide.id && (
                <CardContent className="pt-0 border-t">
                  {/* Symptoms */}
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-slate-700 mb-2">Symptoms:</h4>
                    <ul className="space-y-1">
                      {guide.symptoms.map((symptom, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm text-slate-600">
                          <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                          {symptom}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  {/* Solutions */}
                  <div>
                    <h4 className="text-sm font-medium text-slate-700 mb-2">Step-by-Step Solutions:</h4>
                    <ol className="space-y-3">
                      {guide.solutions.map((solution) => (
                        <li key={solution.step} className="flex gap-3">
                          <span className="flex-shrink-0 w-6 h-6 bg-[#1a365d] text-white rounded-full flex items-center justify-center text-xs font-medium">
                            {solution.step}
                          </span>
                          <div>
                            <p className="font-medium text-slate-800">{solution.title}</p>
                            <p className="text-sm text-slate-600">{solution.description}</p>
                          </div>
                        </li>
                      ))}
                    </ol>
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>

        {/* When to Contact Developer */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <HelpCircle className="h-5 w-5 text-blue-600" /> When to Contact Your Developer
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600 mb-4">
              If you've tried the troubleshooting steps and the issue persists, it's time to escalate. Contact your developer when:
            </p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2 text-sm">
                <XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                <span>The issue affects multiple users or the entire website</span>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                <span>Server health check (/api/health) fails or returns errors</span>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                <span>Data appears to be missing, corrupted, or incorrect</span>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                <span>You've tried all troubleshooting steps without success</span>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                <span>Security-related concerns (suspicious activity, unauthorized access)</span>
              </li>
            </ul>
            
            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>When contacting support, please include:</strong>
              </p>
              <ul className="text-sm text-blue-700 mt-2 space-y-1">
                <li>• What you were trying to do</li>
                <li>• The exact error message (screenshot if possible)</li>
                <li>• What troubleshooting steps you've already tried</li>
                <li>• Your browser and device information</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </main>

      <Footer />
    </div>
  );
};

export default AdminHelpPage;
